import { buildCode39SvgModel } from "./code39-barcode";
import type { LabelPrintFormat } from "./label-print-formats";
import type { LabelPrintRotation } from "./label-print-rotation";
import {
  LABEL_CONTENT_HEIGHT_MM,
  LABEL_CONTENT_WIDTH_MM,
  resolveLabelPrintLayout,
} from "./label-print-formats";

/** Résolution courante imprimantes thermiques 4BARCODE / TSC (203 dpi). */
export const LABEL_PRINT_DPI = 203;

export function mmToPrintPx(mm: number): number {
  return Math.round((mm * LABEL_PRINT_DPI) / 25.4);
}

export interface LabelPrintBitmap {
  dataUrl: string;
  pageWidthMm: number;
  pageHeightMm: number;
  widthPx: number;
  heightPx: number;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }
    if (current) lines.push(current);
    current = word;
    if (lines.length >= maxLines) break;
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines.length > 0 ? lines : [text.slice(0, 32)];
}

function drawBarcode(
  ctx: CanvasRenderingContext2D,
  barcode: string,
  x: number,
  y: number,
  maxWidth: number,
  height: number,
): void {
  const model = buildCode39SvgModel(barcode, 2, height);
  const scale = Math.min(1, maxWidth / model.width);
  const drawHeight = height * scale;
  const drawWidth = model.width * scale;

  ctx.fillStyle = "#000";
  for (const bar of model.bars) {
    ctx.fillRect(
      x + bar.x * scale,
      y,
      Math.max(1, bar.width * scale),
      drawHeight,
    );
  }

  ctx.font = `${Math.round(7.5 * scale + 5)}px ui-monospace, monospace`;
  ctx.textAlign = "center";
  ctx.fillText(model.humanText, x + drawWidth / 2, y + drawHeight + mmToPrintPx(2.5));
  ctx.textAlign = "left";
}

function drawLabelContent(
  ctx: CanvasRenderingContext2D,
  label: {
    productName: string;
    barcode: string;
    sku?: string;
    quantity?: number;
    unit?: string;
  },
  widthPx: number,
  heightPx: number,
): void {
  const pad = mmToPrintPx(3);
  const innerW = widthPx - pad * 2;

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, widthPx, heightPx);

  let y = pad;

  ctx.fillStyle = "#0d9488";
  ctx.font = `bold ${mmToPrintPx(2.2)}px system-ui, sans-serif`;
  ctx.fillText("ITaaS HOSPITOL", pad, y + mmToPrintPx(2.2));
  y += mmToPrintPx(4.5);

  ctx.fillStyle = "#0f172a";
  ctx.font = `bold ${mmToPrintPx(2.8)}px system-ui, sans-serif`;
  for (const line of wrapText(ctx, label.productName, innerW, 2)) {
    ctx.fillText(line, pad, y + mmToPrintPx(2.8));
    y += mmToPrintPx(3.5);
  }

  if (label.sku && label.sku !== label.barcode) {
    ctx.fillStyle = "#64748b";
    ctx.font = `${mmToPrintPx(2.2)}px system-ui, sans-serif`;
    ctx.fillText(`Réf. ${label.sku}`, pad, y + mmToPrintPx(2.2));
    y += mmToPrintPx(3.5);
  }

  const barcodeHeight = mmToPrintPx(11);
  drawBarcode(ctx, label.barcode, pad, y, innerW, barcodeHeight);
  y += barcodeHeight + mmToPrintPx(5);

  if (label.quantity != null && label.quantity > 0) {
    ctx.fillStyle = "#64748b";
    ctx.font = `${mmToPrintPx(2.2)}px system-ui, sans-serif`;
    const qty = `Qté reçue : ${label.quantity}${label.unit ? ` ${label.unit}` : ""}`;
    ctx.fillText(qty, pad, y + mmToPrintPx(2.2));
  }
}

/**
 * Rasterise l'étiquette : format driver + rotation du contenu (pixels).
 */
export function renderLabelPrintBitmap(
  label: {
    productName: string;
    barcode: string;
    sku?: string;
    quantity?: number;
    unit?: string;
  },
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
): LabelPrintBitmap {
  const { pageWidthMm, pageHeightMm, contentWidthMm, contentHeightMm } =
    resolveLabelPrintLayout(format, rotation);

  const pageW = mmToPrintPx(pageWidthMm);
  const pageH = mmToPrintPx(pageHeightMm);
  const contentW = mmToPrintPx(contentWidthMm);
  const contentH = mmToPrintPx(contentHeightMm);
  const designW = mmToPrintPx(LABEL_CONTENT_WIDTH_MM);
  const designH = mmToPrintPx(LABEL_CONTENT_HEIGHT_MM);

  const canvas = document.createElement("canvas");
  canvas.width = pageW;
  canvas.height = pageH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D indisponible pour l'impression étiquette");
  }

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, pageW, pageH);

  const scaleX = contentW / designW;
  const scaleY = contentH / designH;

  ctx.save();
  ctx.translate(pageW / 2, pageH / 2);
  if (rotation !== 0) {
    ctx.rotate((rotation * Math.PI) / 180);
  }
  ctx.translate(-contentW / 2, -contentH / 2);
  ctx.scale(scaleX, scaleY);
  drawLabelContent(ctx, label, designW, designH);
  ctx.restore();

  return {
    dataUrl: canvas.toDataURL("image/png"),
    pageWidthMm,
    pageHeightMm,
    widthPx: pageW,
    heightPx: pageH,
  };
}
